import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mail, Phone, Globe, MapPin, ExternalLink, Images, Play, Trash2, ChevronDown, ChevronUp, Pencil, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { BioRichTextDisplay } from './BioMediaDisplay';

const BioTemplateExecutive = ({ card, bioUrl, constructSocialUrl, trackLinkClick, onQRClick, hideQR = false, isOwner = false, onUpdateProfileImage, onUpdateLogo, isPremium = false }) => {
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxImage]);

  // Color scheme matching the reference
  const goldAccent = '#c9a227';
  const textGold = '#c9a96e';
  const textLight = '#8ec5d0';
  const iconTeal = '#5ba3b0';

  // Brand Icons
  const BrandIcons = {
    linkedin: ({ size = 20 }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#0A66C2">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    twitter: ({ size = 20 }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill={iconTeal}>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    github: ({ size = 20 }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill={iconTeal}>
        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
      </svg>
    ),
    instagram: ({ size = 20 }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill={iconTeal}>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    youtube: ({ size = 20 }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#FF0000">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    facebook: ({ size = 20 }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877F2">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    whatsapp: ({ size = 20 }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#25D366">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    )
  };

  const getSocialLinks = () => {
    const links = [];
    if (card.linkedin) links.push({ type: 'linkedin', value: card.linkedin, label: 'LinkedIn' });
    if (card.twitter) links.push({ type: 'twitter', value: card.twitter, label: 'Twitter' });
    if (card.github) links.push({ type: 'github', value: card.github, label: 'GitHub' });
    if (card.instagram) links.push({ type: 'instagram', value: card.instagram, label: 'Instagram' });
    if (card.youtube) links.push({ type: 'youtube', value: card.youtube, label: 'YouTube' });
    if (card.facebook) links.push({ type: 'facebook', value: card.facebook, label: 'Facebook' });
    if (card.whatsapp) links.push({ type: 'whatsapp', value: card.whatsapp, label: 'WhatsApp' });
    return links;
  };

  const getExtraLinks = () => {
    if (!card.extraLinks) return [];
    return card.extraLinks.filter(link => link.url && link.title);
  };

  const hasGallery = card.galleryImages && card.galleryImages.length > 0;
  const hasVideos = card.youtubeVideos && card.youtubeVideos.length > 0;

  // Icon with circle outline
  const CircleIcon = ({ Icon, color = iconTeal }) => (
    <div 
      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ border: `2px solid ${color}` }}
    >
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
  );

  return (
    <div className="min-h-screen py-4 px-4" style={{ backgroundColor: '#0a1628' }}>
      <div className="max-w-md mx-auto">
        {/* Card Container - Full background image */}
        <div 
          className="rounded-2xl shadow-2xl overflow-hidden"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_5d2c9bbf-12f8-481d-8874-361272cec69e/artifacts/8qax2um1_WhatsApp%20Image%202026-03-04%20at%207.18.49%20PM.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Profile Section */}
          <div className="text-center pt-6 pb-4 px-6">
            {/* Large Circle Profile Image */}
            <div className="relative inline-block mb-5">
              <div 
                className="relative"
                style={{
                  padding: '4px',
                  background: `linear-gradient(135deg, ${goldAccent}, #d4a84b, ${goldAccent})`,
                  borderRadius: '50%',
                  boxShadow: `0 8px 32px rgba(201, 162, 39, 0.3)`
                }}
              >
                {card.profileImage ? (
                  <img 
                    src={card.profileImage} 
                    alt={card.name}
                    className="w-32 h-32 sm:w-36 sm:h-36 rounded-full object-cover"
                    style={{ 
                      border: '3px solid #0a1628'
                    }}
                  />
                ) : (
                  <div 
                    className="w-32 h-32 sm:w-36 sm:h-36 rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: '#132341', 
                      border: '3px solid #0a1628'
                    }}
                  >
                    <span style={{ color: goldAccent }} className="text-5xl font-serif font-bold">
                      {card.name?.charAt(0) || 'E'}
                    </span>
                  </div>
                )}
              </div>
              
              {isOwner && onUpdateProfileImage && (
                <label 
                  htmlFor="profile-upload-executive"
                  className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-amber-50"
                >
                  <Pencil className="w-4 h-4" style={{ color: goldAccent }} />
                  <input id="profile-upload-executive" type="file" accept="image/*" onChange={onUpdateProfileImage} className="hidden" />
                </label>
              )}
            </div>

            {/* Name - Large Gold Text */}
            <h1 
              className="text-2xl sm:text-3xl font-normal tracking-wide mb-1"
              style={{ 
                color: textGold,
                fontFamily: "Georgia, 'Times New Roman', serif",
                letterSpacing: '2px'
              }}
            >
              {card.name?.toUpperCase()}
            </h1>
            
            {/* Title & Company */}
            {(card.title || card.company) && (
              <div 
                className="text-sm tracking-widest mb-6 space-y-1"
                style={{ 
                  color: textLight,
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  fontFamily: "Georgia, 'Times New Roman', serif"
                }}
              >
                {card.title && <p>{card.title}</p>}
                {card.company && <p>{card.company}</p>}
              </div>
            )}
          </div>

          {/* Contact Info - Clean style with circle icons */}
          <div className="px-6 pb-6 space-y-4">
            {card.phone && (
              <a
                href={`tel:${card.phone}`}
                onClick={() => trackLinkClick('phone', card.phone)}
                className="flex items-center gap-4 hover:opacity-80 transition-opacity"
              >
                <CircleIcon Icon={Phone} />
                <span className="text-base" style={{ color: textLight, fontFamily: "Georgia, serif" }}>
                  {card.phone}
                </span>
              </a>
            )}

            {card.email && (
              <a
                href={`mailto:${card.email}`}
                onClick={() => trackLinkClick('email', card.email)}
                className="flex items-center gap-4 hover:opacity-80 transition-opacity"
              >
                <CircleIcon Icon={Mail} />
                <span className="text-base break-all" style={{ color: textLight, fontFamily: "Georgia, serif" }}>
                  {card.email}
                </span>
              </a>
            )}

            {(card.website || (card.websiteName && card.websiteTLD)) && (
              <a
                href={card.website || `https://${card.websiteName}${card.websiteTLD}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 hover:opacity-80 transition-opacity"
              >
                <CircleIcon Icon={Globe} />
                <span className="text-base break-all" style={{ color: textLight, fontFamily: "Georgia, serif" }}>
                  {(card.website || `${card.websiteName}${card.websiteTLD}`).replace(/^https?:\/\//, '')}
                </span>
              </a>
            )}

            {(card.location || card.locationAddress) && (
              <a
                href={
                  card.location && (card.location.startsWith('http://') || card.location.startsWith('https://'))
                    ? card.location 
                    : `https://maps.google.com/?q=${encodeURIComponent(card.locationAddress || card.location || '')}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 hover:opacity-80 transition-opacity"
              >
                <CircleIcon Icon={MapPin} />
                <span className="text-base" style={{ color: textLight, fontFamily: "Georgia, serif" }}>
                  {card.locationAddress || card.location || 'View Location'}
                </span>
              </a>
            )}

            {/* Social Links */}
            {getSocialLinks().length > 0 && (
              <div className="flex justify-center flex-wrap gap-3 pt-4">
                {getSocialLinks().map((social, index) => {
                  const BrandIcon = BrandIcons[social.type];
                  return (
                    <a
                      key={index}
                      href={constructSocialUrl(social.type, social.value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackLinkClick(social.type, social.value)}
                      className="w-11 h-11 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                      style={{ border: `2px solid ${iconTeal}` }}
                      title={social.label}
                    >
                      {BrandIcon && <BrandIcon size={20} />}
                    </a>
                  );
                })}
              </div>
            )}

            {/* Extra Links */}
            {getExtraLinks().length > 0 && (
              <div className="space-y-3 pt-4">
                {getExtraLinks().map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackLinkClick('extraLink', link.url)}
                    className="flex items-center gap-4 p-3 rounded-xl transition-all hover:opacity-90"
                    style={{ backgroundColor: goldAccent }}
                  >
                    <ExternalLink className="w-5 h-5 text-white flex-shrink-0" />
                    <span className="text-base font-medium text-white">{link.title}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bio Section - Outside the main card */}
        {card.richBio && (
          <div 
            className="mt-4 rounded-2xl overflow-hidden"
            style={{ backgroundColor: '#0f1e30', border: `1px solid ${iconTeal}30` }}
          >
            <button
              onClick={() => setIsBioExpanded(!isBioExpanded)}
              className="w-full flex items-center justify-between p-4 hover:opacity-90 transition-opacity"
            >
              <span className="text-sm font-medium tracking-wider" style={{ color: textGold }}>
                ABOUT ME
              </span>
              {isBioExpanded ? (
                <ChevronUp className="w-5 h-5" style={{ color: goldAccent }} />
              ) : (
                <ChevronDown className="w-5 h-5" style={{ color: goldAccent }} />
              )}
            </button>
            <div 
              className={`overflow-hidden transition-all duration-300 ${isBioExpanded ? 'max-h-[500px]' : 'max-h-0'}`}
            >
              <div className="px-4 pb-4 text-sm leading-relaxed" style={{ color: textLight }}>
                <BioRichTextDisplay content={card.richBio} />
              </div>
            </div>
          </div>
        )}

        {/* Gallery & Videos - Below the main card */}
        {(hasGallery || hasVideos) && (
          <div className="mt-4 rounded-2xl overflow-hidden" style={{ backgroundColor: '#0a1628' }}>
            {/* Gallery */}
            {hasGallery && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Images className="w-4 h-4" style={{ color: goldAccent }} />
                  <h3 className="text-sm font-normal tracking-wider" style={{ color: textGold }}>Gallery</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {card.galleryImages.slice(0, 4).map((image, index) => (
                    <div 
                      key={index} 
                      className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer"
                      onClick={() => { setLightboxImage(image); setLightboxIndex(index); }}
                    >
                      <img src={image} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-white/0 group-hover:bg-white/90 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100">
                          <Images className="w-5 h-5 text-gray-700" />
                        </div>
                      </div>
                      {isOwner && card.onDeleteImage && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); card.onDeleteImage(index); }} 
                          className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Videos */}
            {hasVideos && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Play className="w-4 h-4" style={{ color: goldAccent }} />
                  <h3 className="text-sm font-normal tracking-wider" style={{ color: textGold }}>Videos</h3>
                </div>
                <div className="space-y-2">
                  {card.youtubeVideos.slice(0, 3).map((videoId, index) => (
                    <div key={index} className="relative aspect-video rounded-lg overflow-hidden group">
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                        title={`Video ${index + 1}`}
                        className="w-full h-full"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                      {isOwner && card.onDeleteVideo && (
                        <button onClick={() => card.onDeleteVideo(index)} className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lightbox Modal - Full screen using portal */}
        {lightboxImage && createPortal(
          <div 
            className="fixed inset-0 bg-black flex items-center justify-center"
            onClick={() => setLightboxImage(null)}
            style={{ 
              margin: 0, 
              padding: 0, 
              zIndex: 99999,
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}
          >
            {/* Close Button */}
            <button 
              className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 sm:p-3 bg-black/60 hover:bg-white/20 rounded-full transition-colors"
              onClick={() => setLightboxImage(null)}
              style={{ zIndex: 100000 }}
            >
              <X className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
            </button>

            {/* Navigation - Previous */}
            {card.galleryImages && card.galleryImages.length > 1 && (
              <button 
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-black/60 hover:bg-white/20 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = lightboxIndex === 0 ? card.galleryImages.length - 1 : lightboxIndex - 1;
                  setLightboxIndex(newIndex);
                  setLightboxImage(card.galleryImages[newIndex]);
                }}
                style={{ zIndex: 100000 }}
              >
                <ChevronLeft className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
              </button>
            )}

            {/* Image - Full screen */}
            <img 
              src={lightboxImage} 
              alt="Gallery" 
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '100vw', maxHeight: '100vh' }}
            />

            {/* Navigation - Next */}
            {card.galleryImages && card.galleryImages.length > 1 && (
              <button 
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-black/60 hover:bg-white/20 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = lightboxIndex === card.galleryImages.length - 1 ? 0 : lightboxIndex + 1;
                  setLightboxIndex(newIndex);
                  setLightboxImage(card.galleryImages[newIndex]);
                }}
                style={{ zIndex: 100000 }}
              >
                <ChevronRight className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
              </button>
            )}

            {/* Image Counter */}
            {card.galleryImages && card.galleryImages.length > 1 && (
              <div 
                className="absolute bottom-8 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-sm rounded-full text-white text-base font-medium"
                style={{ zIndex: 100000 }}
              >
                {lightboxIndex + 1} / {card.galleryImages.length}
              </div>
            )}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default BioTemplateExecutive;
